// Small formatting + DOM helpers shared by app.ts and graph-view.ts.
import type { JsonAudit, JsonFlow } from './serialize';

export const HUGE = 1e6;

/** Format like the CLI: up to 2 decimals, trailing zeros trimmed. */
export const fmt = (n: number): string => (n >= HUGE ? '∞' : String(Math.round(n * 100) / 100));
export const pct = (e: number): string => `${fmt(e * 100)}%`;
export const isGeneric = (cls?: string) => cls === 'Desc_GenericItem_C' || cls === 'Desc_GenericFluid_C';

export function itemName(a: JsonAudit | undefined, cls: string): string {
  if (cls === '*') return 'anything';
  return a?.items[cls]?.name ?? cls.replace(/^Desc_|_C$/g, '');
}

/** ["90/min Iron Ingot", ...]; unbounded entries become "pass-through". */
export function flowLines(a: JsonAudit | undefined, f: JsonFlow | undefined): string[] {
  if (!f) return [];
  return Object.entries(f)
    .sort((x, y) => y[1] - x[1])
    .map(([cls, v]) => (v >= HUGE ? `unbounded ${itemName(a, cls)}` : `${fmt(v)}/min ${itemName(a, cls)}`));
}
export const flowText = (a: JsonAudit | undefined, f: JsonFlow | undefined, sep = ' + ') => flowLines(a, f).join(sep) || '—';

/** Like flowText but with item icons: [icon, "90/min Iron Ingot", " + ", icon, ...]. */
export function flowNodes(a: JsonAudit | undefined, f: JsonFlow | undefined, iconFn: (cls: string, size?: number) => Node, sep = ' + '): Child[] {
  if (!f || Object.keys(f).length === 0) return ['—'];
  const out: Child[] = [];
  Object.entries(f)
    .sort((x, y) => y[1] - x[1])
    .forEach(([cls, v], i) => {
      if (i) out.push(sep);
      out.push(iconFn(cls, 16), ' ', v >= HUGE ? `unbounded ${itemName(a, cls)}` : `${fmt(v)}/min ${itemName(a, cls)}`);
    });
  return out;
}
export const flowTotal = (f: JsonFlow | undefined) => (f ? Object.values(f).reduce((s, v) => s + v, 0) : 0);

/** "Conveyor Belt Mk.3" -> "Mk.3", "Pipeline Mk.1" -> "Pipe Mk.1", "Conveyor Lift Mk.2" -> "Lift Mk.2" */
export function shortBelt(label: string | undefined): string {
  if (!label) return '';
  return label.replace(/^Conveyor Belt\s*/i, '').replace(/^Conveyor Lift\s*/i, 'Lift ').replace(/^Pipeline\s*/i, 'Pipe ');
}
export function beltText(label: string | undefined, capacity: number | null): string {
  if (capacity === null) return label ? `${shortBelt(label)} (direct)` : 'direct';
  const b = shortBelt(label);
  return b ? `${b} · max ${fmt(capacity)}/min` : `max ${fmt(capacity)}/min`;
}

export type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, string | number | boolean | null | undefined | ((ev: any) => void)>;

function apply(el: Element, attrs: Attrs | null | undefined, children: Child[]) {
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      else if (k === 'class') el.setAttribute('class', String(v));
      else if (k === 'style' && el instanceof HTMLElement) el.style.cssText = String(v);
      else el.setAttribute(k, v === true ? '' : String(v));
    }
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    el.append(c instanceof Node ? c : String(c));
  }
}

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  apply(el, attrs, children);
  return el;
}
export function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs?: Attrs | null, ...children: Child[]): SVGElementTagNameMap[K] {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
  apply(el, attrs, children);
  return el;
}
export function clear(el: Element) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function effClass(eff: number, hasRecipe = true): 'good' | 'warn' | 'bad' | 'none' {
  if (!hasRecipe) return 'none';
  if (eff >= 0.999) return 'good';
  if (eff >= 0.5) return 'warn';
  return 'bad';
}
